(() => {
  // This class handles the search feature inside a collection section
  class CollectionSearch {
    constructor(sectionElement) {
      // Save the current collection section element
      this.sectionElement = sectionElement;

      // Get the search form, search input, and search button inside this section
      this.searchForm = sectionElement.querySelector('[data-collection-search-form]');
      this.searchInput = sectionElement.querySelector('[data-collection-search-input]');
      this.searchButton = this.searchForm?.querySelector('[type="submit"]') || null;

      // Stop everything if the search form or input is missing
      if (!this.searchForm || !this.searchInput) return;

      // Make sure these methods always use the current class instance
      this.onFormSubmit = this.onFormSubmit.bind(this);
      this.onSearchInput = this.onSearchInput.bind(this);
      this.onBrowserBackOrForward = this.onBrowserBackOrForward.bind(this);

      // Get collection page count and current page from HTML data attributes
      this.totalCollectionPages = Number(sectionElement.dataset.collectionTotalPages || 1);
      this.currentCollectionPage = Number(sectionElement.dataset.collectionCurrentPage || 1);

      // Variables used for full collection search across all pages
      this.hasLoadedWideSearchData = false;
      this.loadingWideSearchPromise = null;
      this.allProductsForSearch = [];

      // Used to delay typing search so it does not run on every single keystroke immediately
      this.searchInputDelay = null;
      this.searchDelayTime = 300;

      // Used to track the collection state without the search keyword
      this.savedCollectionStateKey = '';

      // Used to store the original product grid so it can be restored later
      this.originalProductNodes = [];
      this.originalNoResultsNode = null;

      // Start everything needed for the search feature
      this.bindEvents();
      this.updateElementReferences();
      this.saveOriginalGridState();
      this.applySearchFromUrl({ updateInputValue: true });
    }

    bindEvents() {
      // Run search when the form is submitted
      this.searchForm.addEventListener('submit', this.onFormSubmit);

      // Run search while user is typing
      this.searchInput.addEventListener('input', this.onSearchInput);

      // Re-apply search if user uses browser back/forward
      window.addEventListener('popstate', this.onBrowserBackOrForward);
    }

    updateElementReferences() {
      // Refresh important elements inside the collection section
      this.productGrid = this.sectionElement.querySelector('#CollectionLoop');
      this.currentPageItems = Array.from(
        this.sectionElement.querySelectorAll('[data-collection-search-item]')
      );
      this.noResultsMessage = this.sectionElement.querySelector('[data-collection-search-empty]');
      this.paginationWrapper = this.sectionElement.querySelector('[data-collection-pagination]');
    }

    saveOriginalGridState() {
      // Stop if there is no product grid
      if (!this.productGrid) return;

      // Refresh references before saving
      this.updateElementReferences();

      // Clone current products so we can restore them later
      this.originalProductNodes = this.currentPageItems.map((item) => item.cloneNode(true));

      // Save the original "no results" message if it exists
      this.originalNoResultsNode = this.noResultsMessage
        ? this.noResultsMessage.cloneNode(true)
        : null;

      // Save the current collection state without search keyword
      this.savedCollectionStateKey = this.getCollectionStateKeyWithoutSearch();
    }

    getCollectionStateKeyWithoutSearch() {
      // Get the current page URL
      const currentUrl = this.getCurrentPageUrl();
      const urlParams = new URLSearchParams(currentUrl.search);

      // Remove search and page params so only filters/sort remain
      urlParams.delete('q');
      urlParams.delete('page');

      const cleanQuery = urlParams.toString();

      // Return a clean URL string to compare collection state
      return cleanQuery
        ? `${currentUrl.pathname}?${cleanQuery}`
        : currentUrl.pathname;
    }

    onFormSubmit(event) {
      // Stop normal form submit reload behavior
      event.preventDefault();

      // Get the search text
      const searchKeyword = this.searchInput.value.trim();

      // Update URL and run the search
      this.updateUrlSearchParam(searchKeyword);
      this.runSearch(searchKeyword, { updateInputValue: false });
    }

    onSearchInput() {
      // Get the current typed keyword
      const searchKeyword = this.searchInput.value.trim();

      // Clear previous typing delay
      window.clearTimeout(this.searchInputDelay);

      // Wait a little before searching to avoid too many runs
      this.searchInputDelay = window.setTimeout(() => {
        this.updateUrlSearchParam(searchKeyword);
        this.runSearch(searchKeyword, { updateInputValue: false });
      }, this.searchDelayTime);
    }

    onBrowserBackOrForward() {
      // Re-run the search based on the URL when navigating browser history
      this.applySearchFromUrl({ updateInputValue: true });
    }

    getCurrentPageUrl() {
      // Return the current page URL as a URL object
      return new URL(window.location.href);
    }

    getSearchKeywordFromUrl() {
      // Get the search keyword from ?q= in the URL
      const urlParams = this.getCurrentPageUrl().searchParams;
      return (urlParams.get('q') || '').trim();
    }

    updateBaseGridIfCollectionStateChanged() {
      // Get the current collection state without search
      const currentStateKey = this.getCollectionStateKeyWithoutSearch();

      // If nothing changed, do nothing
      if (currentStateKey === this.savedCollectionStateKey) return;

      // If filters/sort/page structure changed, re-save the current grid state
      this.updateElementReferences();
      this.saveOriginalGridState();
    }

    updateUrlSearchParam(searchKeyword) {
      // Get current URL and params
      const currentUrl = this.getCurrentPageUrl();
      const urlParams = currentUrl.searchParams;

      // Remove page number so search always starts fresh
      urlParams.delete('page');

      // Add or remove the search keyword in the URL
      if (searchKeyword) {
        urlParams.set('q', searchKeyword);
      } else {
        urlParams.delete('q');
      }

      // Build the new URL
      const newUrl = urlParams.toString()
        ? `${currentUrl.pathname}?${urlParams.toString()}`
        : currentUrl.pathname;

      // Update browser URL without reloading the page
      window.history.pushState({}, '', newUrl);
    }

    cleanText(value) {
      // Make text easier to compare by trimming spaces and making it lowercase
      return (value || '').toString().trim().toLowerCase();
    }

    showOrHidePagination(shouldHidePagination) {
      // Hide or show pagination depending on search result mode
      if (!this.paginationWrapper) return;
      this.paginationWrapper.hidden = shouldHidePagination;
    }

    setSearchLoadingState(isLoading) {
      // Add/remove a loading class on the collection section
      this.sectionElement.classList.toggle('is-search-loading', isLoading);

      // Disable the search button while loading
      if (this.searchButton) {
        this.searchButton.disabled = isLoading;
      }

      // Add accessibility loading status to the input
      if (this.searchInput) {
        this.searchInput.setAttribute('aria-busy', isLoading ? 'true' : 'false');
      }
    }

    hasFilterParamsInUrl() {
      // Check if the current URL has filter-related params
      const currentUrl = this.getCurrentPageUrl();
      const urlParams = currentUrl.searchParams;

      for (const [paramName, paramValue] of urlParams.entries()) {
        if (!paramValue) continue;

        // Ignore search, page, and sort
        if (paramName === 'q') continue;
        if (paramName === 'page') continue;
        if (paramName === 'sort_by') continue;

        // Detect Shopify filter params
        if (paramName.startsWith('filter.')) {
          return true;
        }

        // Detect constraint param if used by theme
        if (paramName === 'constraint') {
          return true;
        }
      }

      return false;
    }

    shouldUseWideSearch() {
      // Use full collection search only when there are no active filters
      return !this.hasFilterParamsInUrl();
    }

    buildCollectionPageUrl(pageNumber = 1) {
      // Build a clean collection page URL for fetching product data
      const currentUrl = this.getCurrentPageUrl();
      const urlParams = currentUrl.searchParams;

      // Remove search and page params
      urlParams.delete('q');
      urlParams.delete('page');

      // Remove all filter params
      for (const key of [...urlParams.keys()]) {
        if (key.startsWith('filter.')) {
          urlParams.delete(key);
        }
      }

      // Remove constraint param
      urlParams.delete('constraint');

      // Add page param only if page is greater than 1
      if (pageNumber > 1) {
        urlParams.set('page', pageNumber);
      }

      return urlParams.toString()
        ? `${currentUrl.pathname}?${urlParams.toString()}`
        : currentUrl.pathname;
    }

    clearGrid() {
      // Remove everything currently inside the product grid
      if (!this.productGrid) return;
      this.productGrid.replaceChildren();
    }

    createDefaultNoResultsNode() {
      // Create a fallback "no results" message if the theme does not already have one
      const wrapper = document.createElement('div');
      wrapper.innerHTML = `
        <div class="no-results collection-search-empty" data-collection-search-empty>
          <p><strong>No matching products found.</strong></p>
        </div>
      `;
      return wrapper.firstElementChild;
    }

    getProductsFromCurrentPage() {
      // Create a map so duplicate products are not added twice
      const uniqueProducts = new Map();

      this.currentPageItems.forEach((itemElement) => {
        const productId = itemElement.dataset.productId;
        if (!productId || uniqueProducts.has(productId)) return;

        // Save useful product data for searching
        uniqueProducts.set(productId, {
          id: productId,
          searchText: this.cleanText(itemElement.dataset.searchText),
          isAvailable: itemElement.dataset.productAvailable === 'true',
          node: itemElement.cloneNode(true)
        });
      });

      return Array.from(uniqueProducts.values());
    }

    async fetchProductsFromPage(pageNumber) {
      // Load another collection page in the background
      const response = await fetch(this.buildCollectionPageUrl(pageNumber), {
        credentials: 'same-origin',
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      // Stop if request failed
      if (!response.ok) {
        throw new Error(`Failed to load collection page ${pageNumber}`);
      }

      // Convert fetched HTML text into a document we can read
      const responseHtml = await response.text();
      const parser = new DOMParser();
      const parsedDocument = parser.parseFromString(responseHtml, 'text/html');

      // Get all searchable product items from that page
      const pageItems = Array.from(
        parsedDocument.querySelectorAll('#CollectionLoop [data-collection-search-item]')
      );

      // Return clean product data
      return pageItems.map((itemElement) => ({
        id: itemElement.dataset.productId,
        searchText: this.cleanText(itemElement.dataset.searchText),
        isAvailable: itemElement.dataset.productAvailable === 'true',
        node: itemElement.cloneNode(true)
      }));
    }

    async loadAllProductsForWideSearch() {
      // If already loaded before, reuse the saved result
      if (this.hasLoadedWideSearchData) {
        return this.allProductsForSearch;
      }

      // If already loading right now, wait for that same request
      if (this.loadingWideSearchPromise) {
        return this.loadingWideSearchPromise;
      }

      this.loadingWideSearchPromise = (async () => {
        this.setSearchLoadingState(true);

        try {
          const uniqueProducts = new Map();

          // Start with products already on the current page
          this.getProductsFromCurrentPage().forEach((productData) => {
            if (!productData.id || uniqueProducts.has(productData.id)) return;
            uniqueProducts.set(productData.id, productData);
          });

          // If collection has multiple pages, load the rest
          if (this.totalCollectionPages > 1) {
            const pagesToLoad = [];

            for (let pageNumber = 1; pageNumber <= this.totalCollectionPages; pageNumber += 1) {
              if (pageNumber === this.currentCollectionPage) continue;
              pagesToLoad.push(pageNumber);
            }

            // Load pages in small batches to reduce heavy requests
            const batchSize = 3;

            for (let i = 0; i < pagesToLoad.length; i += batchSize) {
              const currentBatch = pagesToLoad.slice(i, i + batchSize);

              const batchResults = await Promise.all(
                currentBatch.map((pageNumber) =>
                  this.fetchProductsFromPage(pageNumber).catch((error) => {
                    console.error(error);
                    return [];
                  })
                )
              );

              // Save unique products only
              batchResults.flat().forEach((productData) => {
                if (!productData.id || uniqueProducts.has(productData.id)) return;
                uniqueProducts.set(productData.id, productData);
              });
            }
          }

          // Save final full product list for searching
          this.allProductsForSearch = Array.from(uniqueProducts.values());
          this.hasLoadedWideSearchData = true;

          return this.allProductsForSearch;
        } finally {
          // Always remove loading state after work is done
          this.setSearchLoadingState(false);
          this.loadingWideSearchPromise = null;
        }
      })();

      return this.loadingWideSearchPromise;
    }

    findMatchesFromWideSearch(searchKeyword) {
      // Clean the keyword before comparing
      const cleanKeyword = this.cleanText(searchKeyword);

      if (!cleanKeyword) {
        return [];
      }

      // Keep available products first, then sold out products after
      const matchedAvailableProducts = [];
      const matchedSoldOutProducts = [];

      this.allProductsForSearch.forEach((productData) => {
        const productMatches = productData.searchText.includes(cleanKeyword);

        if (!productMatches) return;

        if (productData.isAvailable) {
          matchedAvailableProducts.push(productData);
        } else {
          matchedSoldOutProducts.push(productData);
        }
      });

      return [...matchedAvailableProducts, ...matchedSoldOutProducts];
    }

    findMatchesFromCurrentPage(searchKeyword) {
      // Clean the keyword before comparing
      const cleanKeyword = this.cleanText(searchKeyword);

      if (!cleanKeyword) {
        return [];
      }

      // Keep available items first, then sold out items after
      const matchedAvailableItems = [];
      const matchedSoldOutItems = [];

      this.currentPageItems.forEach((itemElement) => {
        const itemSearchText = this.cleanText(itemElement.dataset.searchText);
        const itemIsAvailable = itemElement.dataset.productAvailable === 'true';
        const itemMatches = itemSearchText.includes(cleanKeyword);

        if (!itemMatches) return;

        if (itemIsAvailable) {
          matchedAvailableItems.push(itemElement.cloneNode(true));
        } else {
          matchedSoldOutItems.push(itemElement.cloneNode(true));
        }
      });

      return [...matchedAvailableItems, ...matchedSoldOutItems];
    }

    finalizeRenderedGrid() {
      // Stop if grid does not exist
      if (!this.productGrid) return;

      // Find all images inside the updated product grid
      const images = this.productGrid.querySelectorAll('img');

      images.forEach((img) => {
        // Restore image src if lazy load left it in data-src only
        if (img.dataset.src && !img.getAttribute('src')) {
          img.setAttribute('src', img.dataset.src);
        }

        // Restore image srcset if needed
        if (img.dataset.srcset && !img.getAttribute('srcset')) {
          img.setAttribute('srcset', img.dataset.srcset);
        }

        const markAsLoaded = () => {
          // Remove loading classes and mark image as loaded
          img.classList.remove('loading', 'lazyload', 'lazyloading', 'is-loading');
          img.classList.add('loaded', 'lazyloaded');

          // Also update common image wrapper containers
          const mediaWrapper = img.closest(
            '.media, .card__media, .product-card__image, .image-wrap, .ratio'
          );

          if (mediaWrapper) {
            mediaWrapper.classList.remove('loading', 'is-loading', 'lazyload', 'lazyloading');
            mediaWrapper.classList.add('loaded', 'lazyloaded');
          }

          // Remove busy state from parent loading wrappers
          const loadingParent = img.closest('[aria-busy="true"]');
          if (loadingParent) {
            loadingParent.setAttribute('aria-busy', 'false');
          }
        };

        // If image is already loaded, update classes immediately
        if (img.complete) {
          markAsLoaded();
        } else {
          // Otherwise wait until image loads or fails
          img.addEventListener('load', markAsLoaded, { once: true });
          img.addEventListener(
            'error',
            () => {
              img.classList.remove('loading', 'lazyloading', 'is-loading');
            },
            { once: true }
          );
        }
      });

      // Tell the theme that search rendering is finished
      requestAnimationFrame(() => {
        document.dispatchEvent(
          new CustomEvent('collection:search:rendered', {
            detail: {
              section: this.sectionElement,
              grid: this.productGrid
            }
          })
        );
      });
    }

    renderWideSearchResults(matchedProducts) {
      // Stop if grid does not exist
      if (!this.productGrid) return;

      // Clear current products first
      this.clearGrid();

      const fragment = document.createDocumentFragment();

      // Show no-results message if nothing matched
      if (!matchedProducts.length) {
        const noResultsNode = this.originalNoResultsNode
          ? this.originalNoResultsNode.cloneNode(true)
          : this.createDefaultNoResultsNode();

        noResultsNode.hidden = false;
        fragment.appendChild(noResultsNode);
        this.productGrid.appendChild(fragment);
        this.finalizeRenderedGrid();
        return;
      }

      // Add matched product cards into the grid
      matchedProducts.forEach((productData) => {
        if (!productData.node) return;
        fragment.appendChild(productData.node.cloneNode(true));
      });

      // Keep no-results node in DOM but hidden
      const noResultsNode = this.originalNoResultsNode
        ? this.originalNoResultsNode.cloneNode(true)
        : this.createDefaultNoResultsNode();

      noResultsNode.hidden = true;
      fragment.appendChild(noResultsNode);

      this.productGrid.appendChild(fragment);
      this.finalizeRenderedGrid();
    }

    renderCurrentPageResults(matchedItems) {
      // Stop if grid does not exist
      if (!this.productGrid) return;

      // Clear current products first
      this.clearGrid();

      const fragment = document.createDocumentFragment();

      // Add matched items from current page only
      matchedItems.forEach((itemElement) => {
        fragment.appendChild(itemElement.cloneNode(true));
      });

      // Add hidden or visible no-results message depending on result count
      const noResultsNode = this.originalNoResultsNode
        ? this.originalNoResultsNode.cloneNode(true)
        : this.createDefaultNoResultsNode();

      noResultsNode.hidden = matchedItems.length > 0;
      fragment.appendChild(noResultsNode);

      this.productGrid.appendChild(fragment);
      this.finalizeRenderedGrid();
    }

    restoreOriginalGrid() {
      // Stop if grid does not exist
      if (!this.productGrid) return;

      // Clear current search results
      this.clearGrid();

      const fragment = document.createDocumentFragment();

      // Put back the original saved products
      this.originalProductNodes.forEach((itemNode) => {
        fragment.appendChild(itemNode.cloneNode(true));
      });

      // Put back the original no-results node but keep it hidden
      if (this.originalNoResultsNode) {
        const noResultsNode = this.originalNoResultsNode.cloneNode(true);
        noResultsNode.hidden = true;
        fragment.appendChild(noResultsNode);
      }

      this.productGrid.appendChild(fragment);

      // Show pagination again after restoring original list
      this.showOrHidePagination(false);
      this.updateElementReferences();
      this.finalizeRenderedGrid();
    }

    async runWideSearch(searchKeyword) {
      // Hide pagination because all matching products are shown in one result list
      this.showOrHidePagination(true);

      // Load all collection products, then search through them
      await this.loadAllProductsForWideSearch();
      const matchedProducts = this.findMatchesFromWideSearch(searchKeyword);
      this.renderWideSearchResults(matchedProducts);
    }

    runCurrentPageSearch(searchKeyword) {
      // Refresh elements, search only the current visible page, and hide pagination
      this.updateElementReferences();
      const matchedItems = this.findMatchesFromCurrentPage(searchKeyword);
      this.renderCurrentPageResults(matchedItems);
      this.showOrHidePagination(true);
    }

    async runSearch(searchKeyword, { updateInputValue = false } = {}) {
      // Refresh references and update saved base grid if filters/sort changed
      this.updateElementReferences();
      this.updateBaseGridIfCollectionStateChanged();

      // Stop if grid is missing
      if (!this.productGrid) return;

      // Optionally sync the search input with the keyword
      if (updateInputValue) {
        this.searchInput.value = searchKeyword;
      }

      // Clean keyword for safer comparison
      const cleanKeyword = this.cleanText(searchKeyword);

      // If search is empty, restore the original product grid
      if (!cleanKeyword) {
        this.restoreOriginalGrid();
        return;
      }

      try {
        // Use full collection search if there are no filters,
        // otherwise search only the current filtered page
        if (this.shouldUseWideSearch()) {
          await this.runWideSearch(cleanKeyword);
        } else {
          this.runCurrentPageSearch(cleanKeyword);
        }
      } catch (error) {
        // If something fails, show the original grid again
        console.error('Collection search failed:', error);
        this.restoreOriginalGrid();
      }
    }

    applySearchFromUrl({ updateInputValue = false } = {}) {
      // Read keyword from the URL, then run the matching search
      const searchKeyword = this.getSearchKeywordFromUrl();
      this.runSearch(searchKeyword, { updateInputValue });
    }
  }

  function startCollectionSearch(scope = document) {
    // Find every collection grid that supports this custom search feature
    scope.querySelectorAll('[data-collection-grid]').forEach((sectionElement) => {
      const searchForm = sectionElement.querySelector('[data-collection-search-form]');
      if (!searchForm) return;

      // If already initialized before, just refresh it
      if (sectionElement.collectionSearchInstance) {
        sectionElement.collectionSearchInstance.updateElementReferences();
        sectionElement.collectionSearchInstance.saveOriginalGridState();
        sectionElement.collectionSearchInstance.applySearchFromUrl({ updateInputValue: true });
        return;
      }

      // Otherwise create a new search instance for this section
      sectionElement.collectionSearchInstance = new CollectionSearch(sectionElement);
    });
  }

  // Start search when the page first loads
  document.addEventListener('DOMContentLoaded', () => {
    setTimeout(function(){
      startCollectionSearch(document);
    }, 300);
  });

  // Re-start search when Shopify loads a section in theme editor
  document.addEventListener('shopify:section:load', (event) => {
    startCollectionSearch(event.target);
  });

  // Re-start search for themes that use this custom section loaded event
  document.addEventListener('theme:section:loaded', (event) => {
    startCollectionSearch(event.target);
  });
})();